<script setup lang="ts">
  import { useBranchLogo } from '@/entities/Branch';
  import ArrowLeft from '@/shared/assets/icons/ArrowLeft.svg';
  import ArrowRightIcon from '@/shared/assets/icons/ArrowRightIcon.svg';
  import EraserIcon from '@/shared/assets/icons/EraserIcon.svg';
  import OpenedFolderIcon from '@/shared/assets/icons/OpenedFolderIcon.svg';
  import SaveIcon from '@/shared/assets/icons/SaveIcon.svg';
  import { Loader2 } from 'lucide-vue-next';
  import {
    countFieldsAndTables,
    countFillableFields,
    fillableMatrixProblem,
    pageOrientation,
    repeatedElementsSaveMessage,
    ruleCycleSaveMessage,
    type FormRulesStructure,
    type PageOrientation,
  } from '@record-fill';
  import {
    resetFormBuilderToastQueue,
    useFormBuilderToast,
  } from '../../composables/useFormBuilderToast';
  import { extractApiErrorMessage } from '@/shared/lib';
  import { useNavigateBack } from '@/shared/lib/use';
  import { ColoredButton } from '@/shared/ui';
  import type { ActionMenuItem } from '@/shared/ui/common/ActionMenu.vue';
  import ConfirmDeleteModal from '@/shared/ui/modals/ConfirmDeleteModal.vue';
  import type { SearchComboboxOption } from '@/shared/ui/search-combobox';
  import { computed, onMounted, onUnmounted, provide, ref, watch } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useModal } from 'vue-final-modal';
  import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';
  import {
    MATRIX_HAS_GENERATED_RECORDS_MESSAGE,
    MATRIX_PREFIX_NOT_EDITABLE_MESSAGE,
    MATRIX_RECORD_FIELDS_NOT_EDITABLE_MESSAGE,
    MATRIX_STRUCTURE_INDEXING_LOCKED_MESSAGE,
    isMatrixHasGeneratedRecordsError,
    isMatrixPrefixNotEditableError,
    isMatrixStructureIndexingLockedError,
    matrixBuilderApi,
    matrixCategoryApi,
    matrixModelApi,
    type Matrix,
    type MatrixModel,
  } from '../../api/form-builder.api';
  import { useCanvasHeaderOffset } from '../../composables/useCanvasHeaderOffset';
  import { DRAFT_UPDATED_MESSAGE, useRecordEditMode } from '../../composables/useRecordEditMode';
  import { useFormBuilderApprovalStep } from '../../composables/useFormBuilderApprovalStep';
  import { findDuplicateFieldIds } from '../../lib/field-id-utils';
  import { applyHeaderRecordFields, readHeaderRecordFields } from '../../lib/form-header-fields';
  import { serializeForm, type FormDefinition } from '../../lib/form-serializer';
  import { isKeyboardEventFromEditableTarget } from '../../lib/isKeyboardEventFromEditableTarget';
  import { findPermissionsSubmitProblem } from '../../lib/permissions-submit-check';
  import { useFormBuilderStore } from '../../model/form-builder.store';
  import { useFormPermissionsStore } from '../../model/form-permissions.store';
  import { useFormPermissionsSelectionStore } from '../../model/form-permissions-selection.store';
  import { useFormRulesStore } from '../../model/form-rules.store';
  import { useTableEditorStore } from '../../model/table-editor.store';
  import LoadModelsDialog from '../save-load/LoadModelsDialog.vue';
  import SaveFormDialog from '../save-load/SaveFormDialog.vue';
  import ElementLibrarySidebar from './ElementLibrarySidebar.vue';
  import FormBuilderFooter from './FormBuilderFooter.vue';
  import FormBuilderApprovalStage from './FormBuilderApprovalStage.vue';
  import FormBuilderStageTitle from './FormBuilderStageTitle.vue';
  import FormBuilderPermissionsStage from './FormBuilderPermissionsStage.vue';
  import FormBuilderPermissionsSidebar from '../permissions/FormBuilderPermissionsSidebar.vue';
  import FormBuilderRulesSidebar from './FormBuilderRulesSidebar.vue';
  import FormBuilderRulesStage from './FormBuilderRulesStage.vue';
  import FormCanvas from './FormCanvas.vue';
  import LockedRevisionNotice from './LockedRevisionNotice.vue';
  import PropertyEditorSidebar from './PropertyEditorSidebar.vue';
  import SaveDropdownButton from './SaveDropdownButton.vue';

  const formBuilderStore = useFormBuilderStore();
  const tableEditorStore = useTableEditorStore();
  const formRulesStore = useFormRulesStore();
  const formPermissionsStore = useFormPermissionsStore();
  const permissionsSelectionStore = useFormPermissionsSelectionStore();
  const { showSuccessToast, showErrorToast, showWarningToast } = useFormBuilderToast();
  const router = useRouter();
  const route = useRoute();
  const { navigateBack } = useNavigateBack();
  type FormBuilderStep = 'builder' | 'rules' | 'permissions' | 'approval';
  const currentStep = ref<FormBuilderStep>('builder');

  function parseRouteRecordId(routeName: string): number | null {
    if (route.name !== routeName) return null;
    const raw = route.params.id;
    if (Array.isArray(raw) || !raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  const editingMatrixId = computed<number | null>(() => parseRouteRecordId('EditarFormulario'));
  const editingModelId = computed<number | null>(() => parseRouteRecordId('EditarModelo'));
  const isEditMode = computed(() => editingMatrixId.value !== null);
  const isModelEditMode = computed(() => editingModelId.value !== null);
  // Model editing is its own mode (no rules/permissions/approval, no RQ/REV), but shares the
  // dirty-check / discard-changes-on-exit machinery with matrix editing.
  const isAnyEditMode = computed(() => isEditMode.value || isModelEditMode.value);
  const builderStageTitle = computed(() => {
    if (isModelEditMode.value) return 'Editar Modelo';
    if (isRejectedEditMode.value) return 'Registro Reprovado';
    return isEditMode.value ? 'Editar Registro' : 'Criar Matriz';
  });
  const builderStageSubtitle = computed(() =>
    isRejectedEditMode.value ? 'Edição de Matriz' : undefined
  );

  const loadedMatrix = ref<Matrix | null>(null);
  const isRevisionLocked = computed(
    () => isEditMode.value && (loadedMatrix.value?.generatedRecordsCount ?? 0) > 0
  );
  const loadedModel = ref<MatrixModel | null>(null);
  const loadedSnapshot = ref<string | null>(null);
  const { pageOrientation: orientation } = storeToRefs(formBuilderStore);
  /**
   * Depois de um envio bem-sucedido o Registro ja existe no servidor. As duas guardas da etapa de
   * envio (onBeforeRouteLeave e beforeunload) existem para proteger trabalho NAO persistido, entao
   * a partir daqui as duas param. Sem isso o router.push de handleApprovalSubmitted caia na propria
   * guarda, que abria o modal de confirmacao e cancelava o redirecionamento com next(false).
   */
  const approvalSubmitted = ref(false);
  const isLoadingRecord = ref(false);
  const isSavingRecord = ref(false);
  const editTargetLoadFailed = ref(false);

  const isEditTargetReady = computed(() => {
    if (!isAnyEditMode.value) return true;
    return !isLoadingRecord.value && !editTargetLoadFailed.value;
  });

  const {
    isDraftEditMode,
    isRejectedEditMode,
    lockHeaderRqField,
    exitRoute,
    loadFailure,
    rejectsLoadedMatrix,
    savedDraftQuery,
    lockedApprovalRevision,
    captureRecordFieldsBaseline,
    hasChangedRecordFields,
    hasChangedHomologatedPrefix,
    resolveDraftUpdateErrorMessage,
  } = useRecordEditMode({
    isEditMode,
    loadedMatrix,
    getHeaderRecordFields: () => headerRecordFields.value,
  });

  const isDirty = computed(() => {
    if (!isAnyEditMode.value || loadedSnapshot.value === null) return false;
    return snapshotCurrent() !== loadedSnapshot.value;
  });

  const hasBuilderContentToSave = computed(() => hasElements.value);
  const isRulesHeaderActionDisabled = computed(
    () => isRulesStep.value && formRulesStore.ruleItemSelectionActive
  );

  const isSaveDisabled = computed(() => {
    if (isLoadingRecord.value || isSavingRecord.value) return true;
    if (isPermissionsStep.value && permissionsSelectionStore.addMode) return true;
    if (isRulesHeaderActionDisabled.value) return true;
    if (isPermissionsStep.value)
      return isEditMode.value && !isRevisionLocked.value ? !isDirty.value : false;
    if (isRulesStep.value) return false;
    if (formBuilderStore.hasEmptyFixedText) return true;
    if (formBuilderStore.hasInvalidPropertyEditor) return true;
    return !hasBuilderContentToSave.value;
  });

  function snapshotCurrent(): string {
    // The header is locked and blanked for a model (HTMDOC-2472), but it is also normalized out of
    // the dirty-check here as a safety net: nothing about the header may ever mark a model dirty.
    const elements = isModelEditMode.value
      ? applyHeaderRecordFields(Array.from(formBuilderStore.elements), BLANK_HEADER_RECORD_FIELDS)
      : Array.from(formBuilderStore.elements);
    return JSON.stringify({
      formStructure: {
        ...formBuilderStore.formStructure,
        elements,
      },
      rulesStructure: buildCurrentRulesStructure(),
      permissionsStructure: buildCurrentPermissionsStructure(),
      ruleCreationDraft: formRulesStore.ruleCreationDraft,
      orientation: orientation.value,
    });
  }

  const handleStorageChange = (event: StorageEvent) => {
    if (event.key === 'selectedBranchId') {
      formBuilderStore.reloadForCurrentBranch();
      formPermissionsStore.reloadForCurrentBranch();
      currentStep.value = 'builder';
    }
  };

  function handleHistoryKeyboardShortcut(event: KeyboardEvent) {
    if (
      !(isRulesStep.value || isPermissionsStep.value) ||
      isKeyboardEventFromEditableTarget(event)
    ) {
      return;
    }

    const normalizedKey = event.key.toLowerCase();
    const isModifierPressed = event.ctrlKey || event.metaKey;
    const isUndoShortcut = isModifierPressed && !event.shiftKey && normalizedKey === 'z';
    const isRedoShortcut =
      isModifierPressed && (normalizedKey === 'y' || (event.shiftKey && normalizedKey === 'z'));

    if (!isUndoShortcut && !isRedoShortcut) {
      return;
    }

    event.preventDefault();

    if (isRevisionLocked.value) {
      return;
    }

    if (isUndoShortcut) {
      handleFooterUndo();
      return;
    }

    handleFooterRedo();
  }

  // A model's header has no bearing on the model itself — it only matters once the model is
  // applied while creating a record — so it is always shown blank (HTMDOC-2472).
  const BLANK_HEADER_RECORD_FIELDS = { prefix: '', rev: '', title: '' };

  let currentEditTargetLoad: symbol | null = null;

  function beginEditTargetLoad(): symbol {
    currentEditTargetLoad = Symbol('edit-target-load');
    return currentEditTargetLoad;
  }

  function cancelEditTargetLoad() {
    currentEditTargetLoad = null;
  }

  const isCurrentEditTargetLoad = (load: symbol) => load === currentEditTargetLoad;

  async function loadMatrixForEdit(id: number) {
    const load = beginEditTargetLoad();
    isLoadingRecord.value = true;
    editTargetLoadFailed.value = false;
    try {
      const matrix = await matrixBuilderApi.loadMatrix(id);
      if (!isCurrentEditTargetLoad(load)) return;
      if (rejectsLoadedMatrix(matrix)) {
        failMatrixLoad();
        return;
      }
      loadedMatrix.value = matrix;
      orientation.value = pageOrientation(matrix.matrixStructure);
      const elements = matrix.matrixStructure?.elements ?? [];
      const version = matrix.matrixStructure?.version ?? '1.0';
      formBuilderStore.loadFormStructure({ elements, version });
      formRulesStore.loadPersistedRules({
        rulesStructure: matrix.rulesStructure ?? null,
      });
      formPermissionsStore.loadPersistedPermissions({
        permissionsStructure: matrix.permissionsStructure ?? null,
      });
      captureRecordFieldsBaseline();
      loadedSnapshot.value = snapshotCurrent();
    } catch (error) {
      if (!isCurrentEditTargetLoad(load)) return;
      console.error('Error loading matrix:', error);
      failMatrixLoad();
    } finally {
      if (isCurrentEditTargetLoad(load)) isLoadingRecord.value = false;
    }
  }

  function failMatrixLoad() {
    const { message, target } = loadFailure.value;
    editTargetLoadFailed.value = true;
    showErrorToast({
      title: message,
      type: 'error',
      hideIcon: true,
    });
    router.push(target);
  }

  async function loadModelForEdit(id: number) {
    const load = beginEditTargetLoad();
    isLoadingRecord.value = true;
    editTargetLoadFailed.value = false;
    try {
      const model = await matrixModelApi.getById(id);
      if (!isCurrentEditTargetLoad(load)) return;
      loadedModel.value = model;
      orientation.value = pageOrientation(model.structure);
      // The header carries no data for a model (HTMDOC-2472): blank it on load too, in case a
      // model saved before this rule still has stray header values from the canvas.
      const elements = applyHeaderRecordFields(
        model.structure?.elements ?? [],
        BLANK_HEADER_RECORD_FIELDS
      );
      const version = model.structure?.version ?? '1.0';
      formBuilderStore.loadFormStructure({ elements, version });
      loadedSnapshot.value = snapshotCurrent();
    } catch (error) {
      if (!isCurrentEditTargetLoad(load)) return;
      console.error('Error loading model:', error);
      editTargetLoadFailed.value = true;
      showErrorToast({
        title: 'Erro ao carregar modelo',
        type: 'error',
        hideIcon: true,
      });
      router.push('/gestao-registros/modelos');
    } finally {
      if (isCurrentEditTargetLoad(load)) isLoadingRecord.value = false;
    }
  }

  /**
   * onBeforeRouteLeave e uma guarda do vue-router: nao roda em F5 nem ao fechar a aba. O prompt
   * nativo cobre so a etapa de envio, onde o trabalho ainda nao foi persistido.
   */
  const handleBeforeUnload = (event: BeforeUnloadEvent) => {
    if (!isApprovalStep.value || approvalSubmitted.value) return;
    event.preventDefault();
  };

  onMounted(() => {
    globalThis.addEventListener('storage', handleStorageChange);
    globalThis.addEventListener('keydown', handleHistoryKeyboardShortcut);
    globalThis.addEventListener('beforeunload', handleBeforeUnload);
  });

  type EditTarget = { kind: 'matrix' | 'model'; id: number } | null;

  const editTarget = computed<EditTarget>(() => {
    if (editingMatrixId.value !== null) return { kind: 'matrix', id: editingMatrixId.value };
    if (editingModelId.value !== null) return { kind: 'model', id: editingModelId.value };
    return null;
  });

  watch(
    editTarget,
    (target) => {
      currentStep.value = 'builder';
      if (target?.kind === 'matrix') {
        loadedModel.value = null;
        formBuilderStore.setEditDraftStorage(target.id);
        formRulesStore.setEditDraftStorage(target.id);
        formPermissionsStore.setEditDraftStorage(target.id);
        loadMatrixForEdit(target.id);
        return;
      }
      if (target?.kind === 'model') {
        // Rules/permissions never apply to a model, so their stores/drafts are left untouched.
        loadedMatrix.value = null;
        formBuilderStore.setEditDraftStorage(target.id, 'model');
        loadModelForEdit(target.id);
        return;
      }
      cancelEditTargetLoad();
      isLoadingRecord.value = false;
      editTargetLoadFailed.value = false;
      loadedMatrix.value = null;
      loadedModel.value = null;
      loadedSnapshot.value = null;
      formBuilderStore.initForCreateMode();
      formRulesStore.initForCreateMode();
      formPermissionsStore.initForCreateMode();
    },
    { immediate: true }
  );

  onUnmounted(() => {
    globalThis.removeEventListener('storage', handleStorageChange);
    globalThis.removeEventListener('keydown', handleHistoryKeyboardShortcut);
    globalThis.removeEventListener('beforeunload', handleBeforeUnload);
    resetFormBuilderToastQueue();
  });
  const { currentLogoUrl, handleLogoError } = useBranchLogo();

  const zoom = ref(1);
  provide('formBuilderZoom', zoom);
  const pageCount = ref(1);
  const currentPage = ref(1);
  const showAnchorHighlights = ref(false);
  const showReportHighlights = ref(false);

  const isRulesStep = computed(() => currentStep.value === 'rules');
  const isPermissionsStep = computed(() => currentStep.value === 'permissions');
  const isApprovalStep = computed(() => currentStep.value === 'approval');

  const headerRecordFields = computed(() => readHeaderRecordFields(formBuilderStore.elements));

  /**
   * The header is the single source of RQ in both modes: in edit mode it may differ from the saved
   * prefix, which only "Salvar como" and "Enviar para aprovação" carry into a new Registro. The
   * column only covers a legacy matrix whose header has no RQ, which is why `||` and not `??`: an
   * empty header RQ must fall through. REV has no column of its own
   * (the listing revision is computed by the server), so the header text is its only source.
   */
  const approvalInitialPrefix = computed(
    () =>
      headerRecordFields.value.prefix ||
      (isEditMode.value ? (loadedMatrix.value?.prefix ?? '') : '')
  );
  const approvalInitialRev = computed(
    () => lockedApprovalRevision.value ?? headerRecordFields.value.rev
  );
  // The header title stays editable in edit mode, so it wins over the saved column; the column
  // only covers a legacy matrix whose header never carried a title.
  const recordTitle = computed(
    () =>
      headerRecordFields.value.title ||
      (loadedMatrix.value?.title ?? loadedModel.value?.title ?? '')
  );
  const approvalInitialTitle = computed(() => recordTitle.value);
  const isDraftOnlySaveStep = computed(() => isRulesStep.value || isPermissionsStep.value);
  const saveDialogContext = computed<'matrix' | 'rules' | 'permissions'>(() => {
    if (isPermissionsStep.value) return 'permissions';
    if (isRulesStep.value) return 'rules';
    return 'matrix';
  });

  const builderCanvasContainerRef = ref<HTMLElement | null>(null);

  const { headerStyle: builderHeaderStyle } = useCanvasHeaderOffset(
    builderCanvasContainerRef,
    orientation,
    zoom
  );
  const canUndoFooterAction = computed(() => {
    if (isRevisionLocked.value) return false;
    if (isPermissionsStep.value) return permissionsSelectionStore.canUndo;

    return isRulesStep.value
      ? !formRulesStore.ruleItemSelectionActive && formRulesStore.canUndo
      : formBuilderStore.canUndo;
  });
  const canRedoFooterAction = computed(() => {
    if (isRevisionLocked.value) return false;
    if (isPermissionsStep.value) return permissionsSelectionStore.canRedo;

    return isRulesStep.value
      ? !formRulesStore.ruleItemSelectionActive && formRulesStore.canRedo
      : formBuilderStore.canRedo;
  });
  const footerMode = computed(() => {
    if (isRulesStep.value) return 'rules';
    if (isPermissionsStep.value) return 'permissions';
    return 'builder';
  });

  const hasElements = computed(
    () => formBuilderStore.elements.filter((el) => el.type !== 'HEADER').length > 0
  );
  const isAdvanceDisabled = computed(
    () =>
      isRulesHeaderActionDisabled.value ||
      (!isRulesStep.value &&
        (!hasElements.value ||
          formBuilderStore.hasEmptyFixedText ||
          formBuilderStore.hasInvalidPropertyEditor))
  );
  const totalFieldCount = computed(() =>
    isPermissionsStep.value
      ? countFieldsAndTables(formBuilderStore.elements)
      : countFillableFields(formBuilderStore.elements)
  );
  const totalFieldsScope = computed(() => (isPermissionsStep.value ? 'permissions' : 'matrix'));
  function toggleAnchorHighlights() {
    showAnchorHighlights.value = !showAnchorHighlights.value;
    showReportHighlights.value = false;
  }

  function toggleReportHighlights() {
    showReportHighlights.value = !showReportHighlights.value;
    showAnchorHighlights.value = false;
  }

  function navigateAwayFromEditor() {
    navigateBack(exitRoute.value);
  }

  function openDiscardChangesModal(onConfirmed: () => void) {
    const { open, close } = useModal({
      component: ConfirmDeleteModal,
      attrs: {
        title: 'Descartar alterações?',
        description: isModelEditMode.value
          ? 'As alterações feitas no modelo não foram salvas e serão perdidas.'
          : 'As alterações feitas no registro não foram salvas e serão perdidas.',
        onClose() {
          close();
        },
        onConfirm() {
          close();
          onConfirmed();
        },
      },
    });
    open();
  }

  function handleApprovalSubmitted() {
    approvalSubmitted.value = true;
    formBuilderStore.clearDraft();
    clearRulesDraftAfterSave();
    clearPermissionsDraftAfterSave();
    // Disarms both isDirty and the onBeforeRouteLeave guard so no discard modal fires on the way out.
    loadedSnapshot.value = null;
    router.push({ name: 'GestaoRegistrosListaRegistros' });
  }

  const submittableMatrixId = computed<number | null>(() =>
    loadedMatrix.value?.isDraft === false && !isRejectedEditMode.value
      ? null
      : editingMatrixId.value
  );

  const {
    isSubmittingApproval,
    confirmLeaveApprovalStep,
    confirmCancelApprovalStep,
    submitApproval,
  } = useFormBuilderApprovalStep({
    getMatrixId: () => submittableMatrixId.value,
    getElements: () => Array.from(formBuilderStore.elements),
    getRulesStructure: buildCurrentRulesStructure,
    getPermissionsStructure: buildCurrentPermissionsStructure,
    getOrientation: () => orientation.value,
    returnToPermissions: () => {
      currentStep.value = 'permissions';
    },
    onSubmitted: handleApprovalSubmitted,
  });

  function handleReturn() {
    if (isApprovalStep.value) {
      confirmLeaveApprovalStep();
      return;
    }

    if (isPermissionsStep.value) {
      if (permissionsSelectionStore.addMode) return;
      currentStep.value = 'rules';
      return;
    }

    if (isRulesStep.value) {
      if (formRulesStore.ruleItemSelectionActive) return;
      currentStep.value = 'builder';
      return;
    }

    if (isAnyEditMode.value && isDirty.value) {
      openDiscardChangesModal(() => {
        loadedSnapshot.value = null;
        navigateAwayFromEditor();
      });
      return;
    }

    navigateAwayFromEditor();
  }

  onBeforeRouteLeave((to, _from, next) => {
    // A etapa de envio nao tem rota propria, entao o Voltar do navegador e uma saida do construtor
    // inteiro. Sem este branch as tres clausulas abaixo falham em modo criacao e o formulario se
    // perde sem confirmacao. Nota: num popstate, next(false) + router.push(to) leva ao destino
    // certo mas empilha uma entrada de historico em vez de consumir a de volta.
    if (isApprovalStep.value && !approvalSubmitted.value) {
      confirmLeaveApprovalStep(() => {
        loadedSnapshot.value = null;
        router.push(to);
      });
      next(false);
      return;
    }

    if (!isAnyEditMode.value || !isDirty.value || loadedSnapshot.value === null) {
      next();
      return;
    }
    if (to.name === route.name && to.params.id === route.params.id) {
      next();
      return;
    }
    openDiscardChangesModal(() => {
      loadedSnapshot.value = null;
      router.push(to);
    });
    next(false);
  });

  function validateUniqueFieldIds(): boolean {
    const duplicates = findDuplicateFieldIds(Array.from(formBuilderStore.elements));
    if (duplicates.length === 0) return true;

    const ids = duplicates.map((d) => `"${d.name}"`).join(', ');
    showErrorToast({
      title: `Existem elementos com o mesmo ID do campo: ${ids}. Cada elemento deve ter um ID único.`,
      type: 'error',
      hideIcon: true,
    });
    return false;
  }

  function validateHeaderComplete(): boolean {
    if (isModelEditMode.value) return true;
    // A legacy revision may carry no RQ or REV. in its header; its saved columns stand in for them.
    const headerReady =
      isEditMode.value && !isDraftEditMode.value && !isRejectedEditMode.value
        ? headerRecordFields.value.title !== ''
        : formBuilderStore.isHeaderComplete;
    if (headerReady) return true;

    showWarningToast({
      title: 'Preencha os campos do cabeçalho para prosseguir.',
      type: 'warning',
      hideIcon: true,
    });
    return false;
  }

  function handleAdvance() {
    // Model editing never leaves the builder step: there is no Avançar button to click, but this
    // mirrors the redundant guard pattern used elsewhere in this file (e.g. submitApproval).
    if (isModelEditMode.value) return;
    if (isRulesStep.value) {
      if (!validateMatrixBeforePersistingRules()) return;
      formRulesStore.closeMenu();
      currentStep.value = 'permissions';
      return;
    }

    if (isPermissionsStep.value || isApprovalStep.value) {
      return;
    }

    if (!hasElements.value) {
      return;
    }

    if (!validateHeaderComplete()) {
      return;
    }

    if (!validateMatrixBeforePersistingRules()) {
      return;
    }

    formBuilderStore.selectElement(null);
    tableEditorStore.exitEditingMode();
    if (!isEditMode.value) {
      formRulesStore.loadPersistedRules();
    }
    formRulesStore.reconcileRulesWithElements(formBuilderStore.elements);
    formPermissionsStore.reconcilePermissionsWithElements(formBuilderStore.elements);
    currentStep.value = 'rules';
  }

  function handleClearAction() {
    if (isRulesStep.value) {
      if (formRulesStore.ruleItemSelectionActive) return;
      openClearRulesModal();
      return;
    }

    openClearCanvasModal();
  }

  function openClearRulesModal() {
    if (formRulesStore.rules.length === 0) {
      return;
    }

    const { open, close } = useModal({
      component: ConfirmDeleteModal,
      attrs: {
        icon: EraserIcon,
        iconAlt: 'Limpar Regras',
        title: 'Tem certeza que deseja limpar todas as Regras?',
        description: 'A ação não pode ser desfeita e as edições feitas serão perdidas.',
        onClose() {
          close();
        },
        onConfirm() {
          close();
          try {
            formRulesStore.clearRules();
            showSuccessToast({
              title: 'Todas as Regras foram limpas',
              type: 'success',
            });
          } catch {
            showErrorToast({
              title: 'Erro ao limpar regras. Tente novamente',
              type: 'error',
              hideIcon: true,
            });
          }
        },
      },
    });

    open();
  }

  function handleFooterUndo() {
    if (isRevisionLocked.value) return;
    if (isPermissionsStep.value) {
      permissionsSelectionStore.undo();
      return;
    }

    if (isRulesStep.value) {
      if (formRulesStore.ruleItemSelectionActive) return;
      formRulesStore.undo();
      return;
    }

    formBuilderStore.undo();
  }

  function handleFooterRedo() {
    if (isRevisionLocked.value) return;
    if (isPermissionsStep.value) {
      permissionsSelectionStore.redo();
      return;
    }

    if (isRulesStep.value) {
      if (formRulesStore.ruleItemSelectionActive) return;
      formRulesStore.redo();
      return;
    }

    formBuilderStore.redo();
  }

  async function loadCategoryOptions(): Promise<SearchComboboxOption[]> {
    try {
      const categories = await matrixCategoryApi.list();
      return categories.map((c) => ({ value: String(c.id), label: c.name }));
    } catch (error) {
      console.error('Error loading matrix categories:', error);
      return [];
    }
  }

  function buildCurrentRulesStructure(): FormRulesStructure | null {
    const hasRulesData =
      formRulesStore.rules.length > 0 ||
      formRulesStore.anchorFieldIds.length > 0 ||
      formRulesStore.reportFieldIds.length > 0;

    return hasRulesData ? formRulesStore.buildRulesStructure() : null;
  }

  function buildCurrentPermissionsStructure(): object | null {
    return formPermissionsStore.buildPersistablePermissionsStructure();
  }

  function reconcilePermissionsBeforePersisting() {
    formPermissionsStore.reconcilePermissionsWithElements(formBuilderStore.elements);
  }

  function validatePermissionsBeforePersisting(): boolean {
    const problem = findPermissionsSubmitProblem(
      buildCurrentPermissionsStructure(),
      Array.from(formBuilderStore.elements)
    );
    if (problem === null) return true;

    showWarningToast({ title: problem, type: 'warning', hideIcon: true });
    return false;
  }

  function clearRulesDraftAfterSave() {
    if (formRulesStore.ruleCreationDraft) return;
    formRulesStore.clearDraft();
  }

  function clearPermissionsDraftAfterSave() {
    formPermissionsStore.clearDraft();
  }

  function validateRulesBeforeSave() {
    if (!isRulesStep.value) return true;
    return formRulesStore.validateRulesBeforePersisting();
  }

  function validateMatrixDefinition(): boolean {
    const problem = fillableMatrixProblem(
      formBuilderStore.formStructure,
      buildCurrentRulesStructure()
    );
    if (!problem) return true;

    showErrorToast({
      title:
        problem.kind === 'repeated-ids'
          ? repeatedElementsSaveMessage(problem.labelGroups)
          : ruleCycleSaveMessage(problem.labelGroups),
      type: 'error',
      hideIcon: true,
    });
    return false;
  }

  function validateMatrixBeforePersistingRules() {
    const uniqueNames = validateUniqueFieldIds();
    const definitionOk = validateMatrixDefinition();
    return uniqueNames && definitionOk && validateRulesBeforeSave();
  }

  function openApprovalStep() {
    if (!validateMatrixDefinition()) return;
    currentStep.value = 'approval';
  }

  async function openSaveAsModal() {
    if (!hasElements.value) {
      showWarningToast({
        title: 'Não há elementos para salvar',
        type: 'warning',
        hideIcon: true,
      });
      return;
    }
    if (!isDraftOnlySaveStep.value && !validateHeaderComplete()) {
      return;
    }
    if (!validateMatrixBeforePersistingRules()) {
      return;
    }

    reconcilePermissionsBeforePersisting();
    if (!validatePermissionsBeforePersisting()) {
      return;
    }

    const categoryOptions = await loadCategoryOptions();

    const { open, close } = useModal({
      component: SaveFormDialog,
      attrs: {
        elements: Array.from(formBuilderStore.elements),
        allowedModes: isDraftOnlySaveStep.value ? ['draft'] : ['draft', 'template'],
        saveContext: saveDialogContext.value,
        hideModeSelector: isDraftOnlySaveStep.value,
        rulesStructure: buildCurrentRulesStructure(),
        permissionsStructure: buildCurrentPermissionsStructure(),
        categoryOptions,
        orientation: orientation.value,
        recordPrefix: approvalInitialPrefix.value,
        recordTitle: recordTitle.value,
        onClose() {
          close();
        },
        onHeaderTitleChanged: applyHeaderTitle,
        onSaved(savedId: number, mode: 'draft' | 'template') {
          if (mode === 'draft') {
            handleDraftSavedRedirect(savedId);
          }
        },
      },
    });

    open();
  }

  function applyHeaderTitle(title: string) {
    const header = formBuilderStore.elements.find((element) => element.type === 'HEADER');
    if (!header) return;
    const [updated] = applyHeaderRecordFields([header], {
      ...readHeaderRecordFields([header]),
      title,
    });
    formBuilderStore.updateElement(header.id, { properties: updated.properties });
  }

  function handleDraftSavedRedirect(savedId: number) {
    if (!savedId || savedId <= 0) {
      return;
    }
    formBuilderStore.clearDraft();
    clearRulesDraftAfterSave();
    clearPermissionsDraftAfterSave();
    loadedSnapshot.value = null;
    router.replace({
      name: 'EditarFormulario',
      params: { id: String(savedId) },
      ...(Object.keys(savedDraftQuery.value).length > 0 ? { query: savedDraftQuery.value } : {}),
    });
  }

  async function updateCurrentDraft() {
    if (!isEditMode.value || editingMatrixId.value === null || !loadedMatrix.value) {
      return;
    }
    if (isRevisionLocked.value) {
      showErrorToast({
        title: MATRIX_HAS_GENERATED_RECORDS_MESSAGE,
        type: 'error',
        hideIcon: true,
      });
      return;
    }
    if (hasChangedHomologatedPrefix()) {
      showErrorToast({
        title: MATRIX_PREFIX_NOT_EDITABLE_MESSAGE,
        type: 'error',
        hideIcon: true,
      });
      return;
    }
    if (!hasElements.value) {
      showWarningToast({
        title: 'Não há elementos para salvar',
        type: 'warning',
        hideIcon: true,
      });
      return;
    }
    if (!isDraftOnlySaveStep.value && !validateHeaderComplete()) {
      return;
    }
    if (!validateMatrixBeforePersistingRules()) {
      return;
    }
    if (hasChangedRecordFields()) {
      showErrorToast({
        title: MATRIX_RECORD_FIELDS_NOT_EDITABLE_MESSAGE,
        type: 'error',
        hideIcon: true,
      });
      return;
    }

    reconcilePermissionsBeforePersisting();
    if (!validatePermissionsBeforePersisting()) {
      return;
    }

    const isDraftUpdate = isDraftEditMode.value;
    const recordFields = {
      prefix: loadedMatrix.value.prefix,
      title: recordTitle.value,
    };
    isSavingRecord.value = true;
    try {
      const serialized = serializeForm(
        Array.from(formBuilderStore.elements),
        { ...recordFields, orientation: orientation.value },
        {
          createdAt:
            loadedMatrix.value.matrixStructure?.metadata?.createdAt ?? loadedMatrix.value.createdAt,
        }
      );
      const matrixStructure = JSON.parse(serialized) as FormDefinition;
      const rulesStructure = buildCurrentRulesStructure();
      const permissionsStructure = buildCurrentPermissionsStructure();
      const updated = await matrixBuilderApi.updateMatrix(editingMatrixId.value, {
        ...recordFields,
        matrixStructure,
        rulesStructure,
        permissionsStructure,
      });
      loadedMatrix.value = {
        ...updated,
        rulesStructure: rulesStructure ?? updated.rulesStructure,
        permissionsStructure: permissionsStructure ?? updated.permissionsStructure,
      };
      loadedSnapshot.value = snapshotCurrent();
      formBuilderStore.clearDraft();
      clearRulesDraftAfterSave();
      clearPermissionsDraftAfterSave();
      showSuccessToast({
        title: getMatrixUpdatedMessage(isDraftUpdate),
        type: 'success',
        hideIcon: true,
      });
    } catch (error) {
      console.error('Error updating matrix:', error);
      showErrorToast({
        title: getMatrixUpdateErrorMessage(isDraftUpdate, error),
        type: 'error',
        hideIcon: true,
      });
    } finally {
      isSavingRecord.value = false;
    }
  }

  function getMatrixUpdatedMessage(isDraftUpdate: boolean): string {
    if (isDraftUpdate) return DRAFT_UPDATED_MESSAGE;
    return isDraftOnlySaveStep.value
      ? 'Registro salvo com sucesso.'
      : 'Registro atualizado com sucesso';
  }

  function getMatrixUpdateErrorMessage(isDraftUpdate: boolean, error: unknown): string {
    if (isMatrixHasGeneratedRecordsError(error)) {
      return extractApiErrorMessage(error) ?? MATRIX_HAS_GENERATED_RECORDS_MESSAGE;
    }
    if (isMatrixPrefixNotEditableError(error)) {
      return extractApiErrorMessage(error) ?? MATRIX_PREFIX_NOT_EDITABLE_MESSAGE;
    }
    if (isDraftUpdate) return resolveDraftUpdateErrorMessage(error);
    if (isMatrixStructureIndexingLockedError(error)) return MATRIX_STRUCTURE_INDEXING_LOCKED_MESSAGE;
    return isDraftOnlySaveStep.value
      ? 'Erro ao salvar Registro, tente novamente.'
      : 'Erro ao atualizar registro';
  }

  // Models have no prefix column at all: 'MODELO' is the same placeholder used by
  // SaveFormDialog's saveAsTemplate() to satisfy serializeForm's metadata.prefix requirement.
  const MODEL_STRUCTURE_PREFIX = 'MODELO';

  async function updateModel() {
    if (!isModelEditMode.value || editingModelId.value === null || !loadedModel.value) {
      return;
    }
    if (!hasElements.value) {
      showWarningToast({
        title: 'Não há elementos para salvar',
        type: 'warning',
        hideIcon: true,
      });
      return;
    }
    if (!validateHeaderComplete()) {
      return;
    }
    if (!validateMatrixBeforePersistingRules()) {
      return;
    }

    isSavingRecord.value = true;
    try {
      // The header is locked and blanked on the canvas for a model, but this strips it again
      // defensively: nothing typed into it may ever reach the saved structure (HTMDOC-2472).
      const elementsForSave = applyHeaderRecordFields(
        Array.from(formBuilderStore.elements),
        BLANK_HEADER_RECORD_FIELDS
      );
      const serialized = serializeForm(
        elementsForSave,
        {
          prefix: MODEL_STRUCTURE_PREFIX,
          title: loadedModel.value.title,
          orientation: orientation.value,
        },
        {
          createdAt:
            loadedModel.value.structure?.metadata?.createdAt ?? loadedModel.value.createdAt,
        }
      );
      const structure = JSON.parse(serialized) as FormDefinition;

      const updated = await matrixModelApi.update(editingModelId.value, {
        title: loadedModel.value.title,
        structure,
      });
      loadedModel.value = updated;
      loadedSnapshot.value = snapshotCurrent();
      formBuilderStore.clearDraft();
      showSuccessToast({
        title: 'Modelo atualizado com sucesso',
        type: 'success',
        hideIcon: true,
      });
    } catch (error) {
      console.error('Error updating model:', error);
      showErrorToast({
        title: 'Erro ao atualizar modelo',
        type: 'error',
        hideIcon: true,
      });
    } finally {
      isSavingRecord.value = false;
    }
  }

  async function handleSaveCurrent() {
    if (isModelEditMode.value) {
      await updateModel();
      return;
    }
    if (isEditMode.value) {
      await updateCurrentDraft();
    } else {
      await openSaveAsModal();
    }
  }

  function handleOpenSaveAs() {
    openSaveAsModal();
  }

  const saveMenuItems = computed<ActionMenuItem[]>(() => [
    { id: 'save', label: 'Salvar', onClick: handleSaveCurrent, disabled: isRevisionLocked.value },
    { id: 'save-as', label: 'Salvar como', onClick: handleOpenSaveAs },
  ]);

  const clearActionLabel = computed(() => {
    if (isRulesStep.value) return 'Limpar Regras';
    return isModelEditMode.value ? 'Limpar Modelo' : 'Limpar Registro';
  });

  function handleOpenModels() {
    const { open, close } = useModal({
      component: LoadModelsDialog,
      attrs: {
        onClose() {
          close();
        },
        onAdoptOrientation(value: PageOrientation) {
          orientation.value = value;
        },
      },
    });

    open();
  }

  function getClearCanvasModalTitle(hasAppliedRules: boolean): string {
    if (isModelEditMode.value) return 'Tem certeza que deseja limpar o modelo?';
    if (hasAppliedRules) return 'A Matriz tem regras aplicadas.\nTem certeza que deseja limpar?';
    return 'Tem certeza que deseja limpar a matriz?';
  }

  function openClearCanvasModal() {
    if (!isEditMode.value && !isModelEditMode.value) {
      formRulesStore.loadPersistedRules();
    }
    const hasAppliedRules = !isModelEditMode.value && formRulesStore.rules.length > 0;
    const { open, close } = useModal({
      component: ConfirmDeleteModal,
      attrs: {
        icon: hasAppliedRules ? EraserIcon : undefined,
        iconAlt: hasAppliedRules ? 'Limpar Matriz' : undefined,
        title: getClearCanvasModalTitle(hasAppliedRules),
        description: 'A ação não pode ser desfeita e as edições feitas serão perdidas.',
        onClose() {
          close();
        },
        onConfirm() {
          close();
          formBuilderStore.clearCanvas({ keepRecordFields: isEditMode.value });
          if (!isModelEditMode.value) {
            formRulesStore.clearRules({ clearFieldConfigurations: true });
            formPermissionsStore.clearPermissions();
          }
          showSuccessToast({
            title: isModelEditMode.value ? 'Modelo limpo com sucesso' : 'Matriz limpa com sucesso',
            type: 'success',
          });
        },
      },
    });

    open();
  }

  function openClearPermissionsModal() {
    const { open, close } = useModal({
      component: ConfirmDeleteModal,
      attrs: {
        icon: EraserIcon,
        iconAlt: 'Limpar Permissões',
        title: 'Tem certeza que deseja limpar todas as Permissões?',
        description: 'A ação não pode ser desfeita e as edições feitas serão perdidas.',
        onClose() {
          close();
        },
        onConfirm() {
          close();
          try {
            formPermissionsStore.clearPermissions();
            showSuccessToast({ title: 'Todas as Permissões foram limpas', type: 'success' });
          } catch {
            showErrorToast({ title: 'Erro ao limpar permissões. Tente novamente', type: 'error' });
          }
        },
      },
    });

    open();
  }
</script>

<template>
  <div class="h-screen flex flex-col bg-[var(--color-off-white)] relative">
    <div
      v-if="!isEditTargetReady"
      class="absolute inset-0 z-50 flex items-center justify-center bg-[var(--color-off-white)]"
      data-testid="form-builder-loading"
    >
      <Loader2 class="h-6 w-6 animate-spin text-[var(--theme-secondary-ink)]" />
    </div>
    <header
      class="h-18 bg-white-01 border-b border-[var(--theme-secondary-ink)] px-6 flex items-center justify-between flex-shrink-0"
    >
      <div class="flex items-center gap-15">
        <img
          :src="currentLogoUrl"
          alt="Logo da Filial"
          class="h-10 w-auto object-contain"
          @error="handleLogoError"
        />
        <button
          class="px-4 py-3 rounded-lg font-medium flex items-center gap-3 h-[42px] border border-[var(--theme-secondary-ink)] text-[var(--theme-secondary-ink)]"
          :disabled="
            (isRulesStep && formRulesStore.ruleItemSelectionActive) ||
            (isPermissionsStep && permissionsSelectionStore.addMode)
          "
          @click="handleReturn"
        >
          <component :is="ArrowLeft" alt="Voltar" />
          <span>Voltar</span>
        </button>
      </div>

      <div v-if="!isPermissionsStep && !isApprovalStep" class="flex items-center gap-6">
        <ColoredButton
          :text="clearActionLabel"
          :icon="EraserIcon"
          :icon-alt="clearActionLabel"
          :disabled="
            isRulesStep
              ? isRevisionLocked ||
                formRulesStore.rules.length === 0 ||
                formRulesStore.ruleItemSelectionActive
              : isRevisionLocked || (!hasElements && !formBuilderStore.isHeaderModified)
          "
          :data-testid="isRulesStep ? 'clear-rules-btn' : 'clear-canvas-btn'"
          @click="handleClearAction"
        />
        <ColoredButton
          v-if="!isRulesStep"
          text="Modelos"
          :icon="OpenedFolderIcon"
          icon-alt="Modelos"
          :disabled="isRevisionLocked"
          @click="handleOpenModels"
        />
        <ColoredButton
          v-if="isModelEditMode"
          text="Salvar"
          :icon="SaveIcon"
          icon-alt="Salvar"
          :disabled="isSaveDisabled"
          data-testid="save-model-btn"
          @click="handleSaveCurrent"
        />
        <SaveDropdownButton v-else :items="saveMenuItems" :disabled="isSaveDisabled" />
        <ColoredButton
          v-if="!isModelEditMode"
          text="Avançar"
          :icon="ArrowRightIcon"
          icon-alt="Avançar"
          class="disabled:bg-gray-02 disabled:text-gray-16 disabled:opacity-100"
          :disabled="isAdvanceDisabled"
          @click="handleAdvance"
        />
      </div>

      <div v-else-if="isPermissionsStep" class="flex items-center gap-6">
        <ColoredButton
          text="Limpar Permissões"
          :icon="EraserIcon"
          icon-alt="Limpar Permissões"
          class="disabled:bg-gray-02 disabled:text-gray-16 disabled:opacity-100"
          :disabled="isRevisionLocked || !formPermissionsStore.hasConfiguredPermissions"
          data-testid="clear-permissions-btn"
          @click="openClearPermissionsModal"
        />
        <SaveDropdownButton :items="saveMenuItems" :disabled="isSaveDisabled" />
        <ColoredButton
          text="Enviar para Aprovação"
          data-testid="send-to-approval-btn"
          :disabled="permissionsSelectionStore.addMode"
          @click="openApprovalStep"
        />
      </div>
    </header>

    <FormBuilderApprovalStage
      v-if="isApprovalStep"
      :initial-prefix="approvalInitialPrefix"
      :initial-rev="approvalInitialRev"
      :initial-title="approvalInitialTitle"
      :rev-locked="lockedApprovalRevision !== null"
      :is-submitting="isSubmittingApproval"
      @submit="submitApproval"
      @cancel="confirmCancelApprovalStep"
    />

    <div v-else class="flex flex-1 overflow-hidden bg-[var(--color-off-white)] relative">
      <template v-if="isPermissionsStep">
        <FormBuilderPermissionsStage
          :orientation="orientation"
          :zoom="zoom"
          :locked="isRevisionLocked"
          @update:zoom="zoom = $event"
          @update:pageCount="pageCount = $event"
          @update:currentPage="currentPage = $event"
        />
        <FormBuilderPermissionsSidebar :locked="isRevisionLocked" />
      </template>
      <template v-else-if="isRulesStep">
        <FormBuilderRulesStage
          :orientation="orientation"
          :zoom="zoom"
          :show-anchor-highlights="showAnchorHighlights"
          :show-report-highlights="showReportHighlights"
          :locked="isRevisionLocked"
          @update:zoom="zoom = $event"
          @update:pageCount="pageCount = $event"
          @update:currentPage="currentPage = $event"
        />
        <FormBuilderRulesSidebar :locked="isRevisionLocked" />
      </template>
      <template v-else>
        <ElementLibrarySidebar v-if="!isRevisionLocked" />

        <div
          ref="builderCanvasContainerRef"
          class="flex flex-col flex-1 overflow-hidden min-w-0 bg-gray-100"
        >
          <div class="py-4" :style="builderHeaderStyle">
            <FormBuilderStageTitle :title="builderStageTitle" :subtitle="builderStageSubtitle" />
            <LockedRevisionNotice v-if="isRevisionLocked" />
          </div>

          <FormCanvas
            v-model:orientation="orientation"
            v-model:zoom="zoom"
            :show-anchor-highlights="showAnchorHighlights"
            :show-report-highlights="showReportHighlights"
            :lock-header-rq-field="lockHeaderRqField"
            :header-readonly="isModelEditMode"
            :locked="isRevisionLocked"
            :force-readonly="isRevisionLocked"
            @update:pageCount="pageCount = $event"
            @update:currentPage="currentPage = $event"
          />
        </div>

        <PropertyEditorSidebar
          v-if="!isRevisionLocked"
          class="absolute right-0 top-0 h-full z-10"
        />
      </template>
    </div>

    <FormBuilderFooter
      v-if="!isApprovalStep"
      :zoom="zoom"
      :orientation="orientation"
      :page-count="pageCount"
      :mode="footerMode"
      :total-fields="totalFieldCount"
      :total-fields-scope="totalFieldsScope"
      :current-page="currentPage"
      :can-undo="canUndoFooterAction"
      :can-redo="canRedoFooterAction"
      :show-anchor-highlights="showAnchorHighlights"
      :show-report-highlights="showReportHighlights"
      @update:zoom="zoom = $event"
      @update:orientation="orientation = $event"
      @undo="handleFooterUndo"
      @redo="handleFooterRedo"
      @toggle-anchor-visibility="toggleAnchorHighlights"
      @toggle-report-visibility="toggleReportHighlights"
    />
  </div>
</template>
